import React, { FunctionComponent, ReactNode } from 'react';

import { useMedia } from 'react-use';
import styled from 'styled-components';

import { Button } from 'chime-ui/components/Button';
import { ErrorIcon } from 'chime-ui/components/Icons';
import { SmallTitle, StandardText } from 'chime-ui/components/Typography';
import media, { screenSizes } from 'chime-ui/system/media';

import Layout, {
  Content,
  ContentWrapper,
  CopyrightText,
} from 'components/Layout';
import Header from 'components/Layout/Header';

type ErrorGenericProps = {
  children?: ReactNode;
  cta?: ReactNode;
  showLogin?: boolean;
};

const ErrorContent = styled(Content)`
  padding: 3rem 0 0;
`;

const ErrorGeneric: FunctionComponent<ErrorGenericProps> = ({
  children = <DefaultError />,
  cta = (
    <Button
      as="a"
      href="https://help.chime.com/hc/en-us/articles/23918862092567-How-can-I-troubleshoot-issues-with-claiming-Pay-Anyone-Transfers-if-I-m-not-a-Chime-member"
    >
      Get Help
    </Button>
  ),
  showLogin,
}) => {
  const isMobileScreenSize = useMedia(
    `(max-width: ${screenSizes.tablet - 1}px)`
  );

  const FooterWithCTA = () => (
    <FooterLayout>
      {!!cta && <CTALayout>{cta}</CTALayout>}
      <CopyrightLayout>{<CopyrightText />}</CopyrightLayout>
    </FooterLayout>
  );

  return (
    <>
      <Layout
        header={<Header showLogin={showLogin} />}
        footer={isMobileScreenSize && <FooterWithCTA />}
      >
        <ContentWrapper>
          <ErrorContent>
            <ErrorIcon />
            {children}
          </ErrorContent>
        </ContentWrapper>
        {!isMobileScreenSize && <CTALayout>{cta}</CTALayout>}
      </Layout>
    </>
  );
};

export default ErrorGeneric;

export const DefaultError: FunctionComponent = () => (
  <>
    <SmallTitle>Something went wrong</SmallTitle>
    <StandardText>
      We&apos;re working on it. Please try again later or contact our Member
      Services team if you need more support.
    </StandardText>
  </>
);

const CTALayout = styled.div`
  padding: 1.5rem 0;

  ${media.mobileOnly`
    padding: 1.5rem;
  `}
`;

const CopyrightLayout = styled.div`
  text-align: center;
  font-size: 9px;
  margin: 0 0 0.5rem 0;
`;

const FooterLayout = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
`;
